/**
 * CORE-629 — a read-state changed signal, so the unread badges can refresh the
 * moment a thread is marked read instead of waiting out the 30 s poll in
 * {@link ../../hooks/useChatApiUnreadCounts}.
 *
 * Why a module-level emitter and not context: the producer is a transport class
 * ({@link ./chatApiChannel.ChatApiChannel}) that lives outside the React tree,
 * and the consumer is a hook mounted in the header. Threading a callback from
 * one to the other would mean plumbing through every chat panel.
 *
 * The signal carries no payload — it only says "your unread numbers are stale".
 * The badge re-reads the authoritative count from the server, so a duplicate or
 * spurious notify costs one cheap GET and can never desync the UI.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe to read-state changes. Returns the unsubscribe. */
export function onUnreadChanged(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Announce that the caller's read state moved (a thread was marked read). */
export function notifyUnreadChanged(): void {
  listeners.forEach((cb) => {
    // One badge throwing must not stop the others from refreshing.
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}
